import { useState } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useSelector, useDispatch } from "react-redux";
import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonBackButton,
  IonProgressBar,
  IonPage,
  IonTitle,
  IonToolbar,
  IonList,
  IonItem,
  IonCheckbox,
  IonTextarea,
  IonSpinner
} from '@ionic/react';
import classnames from 'classnames';

import { getLoad, cancelLoad } from '@/actions/loads';
import { loadNumber } from '@/helpers/humanize';
import { preventDefault } from '@/helpers/events';
import useResourceLoader from '@/hooks/useResourceLoader';

import NotFoundPage from '@/pages/NotFoundPage';
import ValidatedInput from '@/components/ValidatedInput';

export default function CancelPage() {
  const {uuid} = useParams();
  const history = useHistory();
  const dispatch = useDispatch();

  const loading = useResourceLoader(getLoad(uuid));
  const load = useSelector((state) => _.find(state.loads, {uuid}));

  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonTouched, setCancelReasonTouched] = useState(false);
  const [refund, setRefund] = useState(null);
  const refundValid = !refund || (refund * 100) <= load.charged_cents && refund >= 0;

  const [saving, setSaving] = useState(false);
  const cancelReasonValid = !!(cancelReason.length ?? 0 > 0);
  const valid = cancelReasonValid && refundValid;

  const submitCancel = async () => {
    if (!valid) return;

    try {
      setSaving(true);
      await dispatch(cancelLoad({uuid, cancelReason, refundCents: _.round(refund * 100)}));
      history.goBack();
    } catch (e) {
      setSaving(false);
      alert(_.get(e, 'response.data.error', e.message));
    }
  }

  if (!loading && !load) return <NotFoundPage type="Load"/>;

  return load && (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref={`/loads/${uuid}`}/>
          </IonButtons>
          <IonTitle>Cancel Load</IonTitle>
          {(loading || saving) && <IonProgressBar type="indeterminate"/>}
        </IonToolbar>
      </IonHeader>
      <IonContent fullscreen>
        <form onSubmit={preventDefault(submitCancel)}>
          <IonList>
            <IonItem lines="none">
              <h4>Load {loadNumber(load.number)}</h4>
            </IonItem>
            <IonItem lines="none">
              <IonTextarea label="Cancel Reason (this will be sent to the customer)"
                name="cancel_reason"
                value={cancelReason}
                errorText="Enter a cancelation reason"
                onIonInput={(e) => setCancelReason(e.target.value)}
                onIonBlur={(e) => setCancelReasonTouched(true)}
                className={classnames({'ion-valid': cancelReasonValid, 'ion-invalid': !cancelReasonValid, 'ion-touched': cancelReasonTouched})}
                labelPlacement="floating"
                autoGrow/>
            </IonItem>
            {load.charged_cents > 0 && (
              <IonItem lines="none">
                <IonCheckbox
                  justify="start"
                  labelPlacement="end"
                  slot="start"
                  checked={!_.isNil(refund)}
                  onIonChange={(e) => setRefund(e.target.checked ? load.charged_cents / 100 : null)}
                >Issue Refund
                </IonCheckbox>
                {!_.isNil(refund) && (
                  <ValidatedInput
                    label="Refund ($)"
                    type="number"
                    name="refund"
                    value={refund}
                    setValue={(value) => setRefund(value)}
                    valid={refundValid}
                    errorText="The refund cannot be more than the outstanding charge or less than 0"
                    step="0.01"
                    min={0}
                    max={load.charged_cents/100}/>
                )}
              </IonItem>
            )}
          </IonList>
          <IonButton type="submit" expand="block" className="ion-padding" color="warning" disabled={!valid || saving}>
            {saving ? <IonSpinner name="dots"/> : "Confirm Cancel"}
          </IonButton>
        </form>
      </IonContent>
    </IonPage>
  );
};
